import * as React from "react";
import { cn } from "../../../lib/utils";
import {
  AlertTriangle,
  Shield,
  ShieldAlert,
  ShieldCheck,
  Globe,
  Mail,
  Server,
  Clock,
  Eye,
  Search,
  Filter,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Copy,
  Check,
} from "lucide-react";
import { ModuleContainer, ModuleSection } from "../ModuleContainer";
import { ModuleScore } from "../ModuleScore";
import { Badge } from "../../badge";
import { Button } from "../../button";
import { Card, CardContent, CardHeader, CardTitle } from "../../card";
import { Input } from "../../input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../select";

// Typosquat Module Data Types
type TyposquatRisk = "info" | "low" | "medium" | "high" | "critical";

type TyposquatTechnique =
  | "homoglyph"
  | "keyboard_adjacent"
  | "omission"
  | "addition"
  | "transposition"
  | "replacement"
  | "tld_variation"
  | "hyphenation"
  | "bit_flip"
  | "repetition"
  | "vowel_swap"
  | "subdomain_prefix";

interface TyposquatDomain {
  domain: string;
  technique: TyposquatTechnique;
  is_registered: boolean;
  ips?: string[];
  has_mx?: boolean;
  whois_created?: string;
  risk: TyposquatRisk;
  notes?: string[];
}

export interface TyposquatModuleData {
  target: string;
  generated_count: number;
  registered_count: number;
  typosquats: TyposquatDomain[];
  score: number;
  issues?: string[];
  duration_ms?: number;
}

export interface TyposquatModuleViewProps {
  data: TyposquatModuleData;
  isLoading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  className?: string;
  /** Hide the header (useful when rendered within tabs) */
  hideHeader?: boolean;
}

// Risk level configuration
const RISK_CONFIG: Record<TyposquatRisk, { label: string; color: string; bgColor: string; borderColor: string; icon: typeof ShieldCheck }> = {
  info: {
    label: "Info",
    color: "text-slate-400",
    bgColor: "bg-slate-500/10",
    borderColor: "border-slate-500/30",
    icon: Shield,
  },
  low: {
    label: "Low",
    color: "text-cyan-400",
    bgColor: "bg-cyan-500/10",
    borderColor: "border-cyan-500/30",
    icon: Shield,
  },
  medium: {
    label: "Medium",
    color: "text-amber-400",
    bgColor: "bg-amber-500/10",
    borderColor: "border-amber-500/30",
    icon: Shield,
  },
  high: {
    label: "High",
    color: "text-orange-400",
    bgColor: "bg-orange-500/10",
    borderColor: "border-orange-500/30",
    icon: ShieldAlert,
  },
  critical: {
    label: "Critical",
    color: "text-red-400",
    bgColor: "bg-red-500/10",
    borderColor: "border-red-500/30",
    icon: ShieldAlert,
  },
};

// Technique labels and descriptions
const TECHNIQUE_INFO: Record<TyposquatTechnique, { label: string; description: string }> = {
  homoglyph: { label: "Homoglyph", description: "Visual lookalike characters (l→1, o→0)" },
  keyboard_adjacent: { label: "Keyboard", description: "Adjacent keyboard key substitution" },
  omission: { label: "Omission", description: "Missing character" },
  addition: { label: "Addition", description: "Extra character inserted" },
  transposition: { label: "Transposition", description: "Swapped adjacent characters" },
  replacement: { label: "Replacement", description: "Single character replacement" },
  tld_variation: { label: "TLD Variation", description: "Different top-level domain" },
  hyphenation: { label: "Hyphenation", description: "Hyphen added or removed" },
  bit_flip: { label: "Bit Flip", description: "Bit-flip character mutation" },
  repetition: { label: "Repetition", description: "Repeated character" },
  vowel_swap: { label: "Vowel Swap", description: "Vowel substitution" },
  subdomain_prefix: { label: "Subdomain", description: "Subdomain prefix trick" },
};

// Summary Stats Card
function SummaryStats({ data }: { data: TyposquatModuleData }) {
  const criticalCount = data.typosquats.filter((t) => t.risk === "critical").length;
  const highCount = data.typosquats.filter((t) => t.risk === "high").length;
  const withMxCount = data.typosquats.filter((t) => t.has_mx).length;

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <Card className="bg-card/50">
        <CardHeader className="pb-2">
          <CardTitle className="text-xs text-muted-foreground font-normal uppercase tracking-wider">
            Generated
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-2xl font-bold font-mono">{data.generated_count.toLocaleString()}</p>
          <p className="text-xs text-muted-foreground">permutations checked</p>
        </CardContent>
      </Card>

      <Card className={cn("bg-card/50", data.registered_count > 0 && "border-amber-500/30")}>
        <CardHeader className="pb-2">
          <CardTitle className="text-xs text-muted-foreground font-normal uppercase tracking-wider">
            Registered
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className={cn("text-2xl font-bold font-mono", data.registered_count > 0 ? "text-amber-400" : "text-emerald-400")}>
            {data.registered_count}
          </p>
          <p className="text-xs text-muted-foreground">typosquat domains</p>
        </CardContent>
      </Card>

      <Card className={cn("bg-card/50", criticalCount + highCount > 0 && "border-red-500/30")}>
        <CardHeader className="pb-2">
          <CardTitle className="text-xs text-muted-foreground font-normal uppercase tracking-wider">
            High Risk
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className={cn("text-2xl font-bold font-mono", criticalCount + highCount > 0 ? "text-red-400" : "text-emerald-400")}>
            {criticalCount + highCount}
          </p>
          <p className="text-xs text-muted-foreground">
            {criticalCount > 0 && `${criticalCount} critical`}
            {criticalCount > 0 && highCount > 0 && ", "}
            {highCount > 0 && `${highCount} high`}
            {criticalCount === 0 && highCount === 0 && "none detected"}
          </p>
        </CardContent>
      </Card>

      <Card className={cn("bg-card/50", withMxCount > 0 && "border-orange-500/30")}>
        <CardHeader className="pb-2">
          <CardTitle className="text-xs text-muted-foreground font-normal uppercase tracking-wider">
            With Email
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className={cn("text-2xl font-bold font-mono", withMxCount > 0 ? "text-orange-400" : "text-muted-foreground")}>
            {withMxCount}
          </p>
          <p className="text-xs text-muted-foreground">can receive email (MX)</p>
        </CardContent>
      </Card>
    </div>
  );
}

// Risk Distribution Chart
function RiskDistribution({ typosquats }: { typosquats: TyposquatDomain[] }) {
  const distribution = {
    critical: typosquats.filter((t) => t.risk === "critical").length,
    high: typosquats.filter((t) => t.risk === "high").length,
    medium: typosquats.filter((t) => t.risk === "medium").length,
    low: typosquats.filter((t) => t.risk === "low").length,
    info: typosquats.filter((t) => t.risk === "info").length,
  };

  const total = Object.values(distribution).reduce((a, b) => a + b, 0);
  if (total === 0) return null;

  return (
    <div className="space-y-3">
      <div className="flex h-3 rounded-full overflow-hidden bg-muted">
        {(["critical", "high", "medium", "low", "info"] as TyposquatRisk[]).map((risk) => {
          const count = distribution[risk];
          if (count === 0) return null;
          const width = (count / total) * 100;
          const config = RISK_CONFIG[risk];
          return (
            <div
              key={risk}
              className={cn("h-full", config.bgColor.replace("/10", "/60"))}
              style={{ width: `${width}%` }}
              title={`${config.label}: ${count}`}
            />
          );
        })}
      </div>
      <div className="flex flex-wrap gap-4 text-xs">
        {(["critical", "high", "medium", "low", "info"] as TyposquatRisk[]).map((risk) => {
          const count = distribution[risk];
          if (count === 0) return null;
          const config = RISK_CONFIG[risk];
          return (
            <div key={risk} className="flex items-center gap-1.5">
              <div className={cn("w-2.5 h-2.5 rounded-full", config.bgColor.replace("/10", "/60"))} />
              <span className="text-muted-foreground">
                {config.label}: <span className={config.color}>{count}</span>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Individual Typosquat Row
function TyposquatRow({ domain }: { domain: TyposquatDomain }) {
  const [expanded, setExpanded] = React.useState(false);
  const [copied, setCopied] = React.useState(false);
  const config = RISK_CONFIG[domain.risk];
  const techniqueInfo = TECHNIQUE_INFO[domain.technique];

  const handleCopy = () => {
    navigator.clipboard.writeText(domain.domain);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Calculate days since registration
  const daysSinceRegistration = domain.whois_created
    ? Math.floor((Date.now() - new Date(domain.whois_created).getTime()) / (1000 * 60 * 60 * 24))
    : null;

  return (
    <div className={cn("rounded-lg border", config.borderColor, config.bgColor)}>
      <div
        className="flex items-center gap-3 p-3 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <button className="p-1 rounded hover:bg-white/5">
          {expanded ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          )}
        </button>

        <config.icon className={cn("h-4 w-4 flex-shrink-0", config.color)} />

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm font-medium truncate">{domain.domain}</span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleCopy();
              }}
              className="p-1 rounded hover:bg-white/10"
              title="Copy domain"
            >
              {copied ? (
                <Check className="h-3 w-3 text-emerald-400" />
              ) : (
                <Copy className="h-3 w-3 text-muted-foreground" />
              )}
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {domain.has_mx && (
            <Badge variant="outline" className="text-xs bg-orange-500/10 text-orange-400 border-orange-500/30">
              <Mail className="h-3 w-3 mr-1" />
              MX
            </Badge>
          )}
          {domain.is_registered && domain.ips && domain.ips.length > 0 && (
            <Badge variant="outline" className="text-xs bg-cyan-500/10 text-cyan-400 border-cyan-500/30">
              <Server className="h-3 w-3 mr-1" />
              {domain.ips.length} IP{domain.ips.length > 1 ? "s" : ""}
            </Badge>
          )}
          <Badge variant="outline" className={cn("text-xs", config.bgColor, config.color, config.borderColor)}>
            {config.label}
          </Badge>
        </div>
      </div>

      {expanded && (
        <div className="px-3 pb-3 pt-0 border-t border-border/50 mt-0">
          <div className="grid gap-3 sm:grid-cols-2 pt-3">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Technique</p>
              <p className="text-sm font-medium">{techniqueInfo.label}</p>
              <p className="text-xs text-muted-foreground">{techniqueInfo.description}</p>
            </div>

            {domain.is_registered && (
              <div>
                <p className="text-xs text-muted-foreground mb-1">Registration</p>
                {domain.whois_created ? (
                  <>
                    <p className="text-sm font-medium">
                      {new Date(domain.whois_created).toLocaleDateString()}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {daysSinceRegistration !== null && (
                        <>
                          {daysSinceRegistration < 365 ? (
                            <span className="text-amber-400">
                              {daysSinceRegistration} days ago (recent)
                            </span>
                          ) : (
                            <span>{Math.floor(daysSinceRegistration / 365)} years ago</span>
                          )}
                        </>
                      )}
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">Unknown</p>
                )}
              </div>
            )}

            {domain.ips && domain.ips.length > 0 && (
              <div className="sm:col-span-2">
                <p className="text-xs text-muted-foreground mb-1">IP Addresses</p>
                <div className="flex flex-wrap gap-1">
                  {domain.ips.map((ip) => (
                    <Badge key={ip} variant="outline" className="text-xs font-mono">
                      {ip}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {domain.notes && domain.notes.length > 0 && (
              <div className="sm:col-span-2">
                <p className="text-xs text-muted-foreground mb-1">Notes</p>
                <ul className="text-xs space-y-1">
                  {domain.notes.map((note, i) => (
                    <li key={i} className="flex items-start gap-1">
                      <AlertTriangle className="h-3 w-3 text-amber-400 mt-0.5 flex-shrink-0" />
                      <span>{note}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="flex gap-2 mt-3 pt-3 border-t border-border/50">
            <Button
              variant="outline"
              size="sm"
              className="text-xs"
              onClick={() => window.open(`https://${domain.domain}`, "_blank")}
            >
              <ExternalLink className="h-3 w-3 mr-1" />
              Visit Site
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="text-xs"
              onClick={() => window.open(`https://who.is/whois/${domain.domain}`, "_blank")}
            >
              <Eye className="h-3 w-3 mr-1" />
              WHOIS Lookup
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// Typosquat List with Filtering
function TyposquatList({ typosquats }: { typosquats: TyposquatDomain[] }) {
  const [searchTerm, setSearchTerm] = React.useState("");
  const [riskFilter, setRiskFilter] = React.useState<TyposquatRisk | "all">("all");
  const [techniqueFilter, setTechniqueFilter] = React.useState<TyposquatTechnique | "all">("all");
  const [showRegisteredOnly, setShowRegisteredOnly] = React.useState(true);

  const filteredDomains = React.useMemo(() => {
    return typosquats.filter((domain) => {
      if (showRegisteredOnly && !domain.is_registered) return false;
      if (searchTerm && !domain.domain.toLowerCase().includes(searchTerm.toLowerCase())) return false;
      if (riskFilter !== "all" && domain.risk !== riskFilter) return false;
      if (techniqueFilter !== "all" && domain.technique !== techniqueFilter) return false;
      return true;
    });
  }, [typosquats, searchTerm, riskFilter, techniqueFilter, showRegisteredOnly]);

  // Get unique techniques for filter
  const techniques = React.useMemo(() => {
    const unique = new Set(typosquats.map((t) => t.technique));
    return Array.from(unique).sort();
  }, [typosquats]);

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search domains..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        <Select value={riskFilter} onValueChange={(v) => setRiskFilter(v as TyposquatRisk | "all")}>
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Risk Level" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Risks</SelectItem>
            <SelectItem value="critical">Critical</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="low">Low</SelectItem>
            <SelectItem value="info">Info</SelectItem>
          </SelectContent>
        </Select>

        <Select value={techniqueFilter} onValueChange={(v) => setTechniqueFilter(v as TyposquatTechnique | "all")}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Technique" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Techniques</SelectItem>
            {techniques.map((tech) => (
              <SelectItem key={tech} value={tech}>
                {TECHNIQUE_INFO[tech]?.label || tech}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button
          variant={showRegisteredOnly ? "secondary" : "outline"}
          size="sm"
          onClick={() => setShowRegisteredOnly(!showRegisteredOnly)}
          className="text-xs"
        >
          <Filter className="h-3 w-3 mr-1" />
          Registered Only
        </Button>
      </div>

      {/* Results Count */}
      <div className="text-xs text-muted-foreground">
        Showing {filteredDomains.length} of {typosquats.length} typosquats
        {showRegisteredOnly && ` (${typosquats.filter((t) => t.is_registered).length} registered)`}
      </div>

      {/* List */}
      <div className="space-y-2">
        {filteredDomains.length > 0 ? (
          filteredDomains.map((domain) => (
            <TyposquatRow key={domain.domain} domain={domain} />
          ))
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <Globe className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>No typosquats match your filters</p>
          </div>
        )}
      </div>
    </div>
  );
}

// Main Component
export function TyposquatModuleView({
  data,
  isLoading,
  error,
  onRetry,
  className,
  hideHeader,
}: TyposquatModuleViewProps) {
  const hasThreats = data.registered_count > 0;
  const hasCritical = data.typosquats.some((t) => t.risk === "critical");

  return (
    <ModuleContainer
      moduleName="typosquat"
      icon={hasThreats ? AlertTriangle : ShieldCheck}
      data={data}
      isLoading={isLoading}
      error={error}
      onRetry={onRetry}
      className={className}
      hideHeader={hideHeader}
    >
      <div className="space-y-6">
        {/* Header with Target and Score */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">Target Domain</p>
            <p className="text-xl font-mono font-bold">{data.target}</p>
          </div>
          <ModuleScore
            score={data.score}
            size="lg"
          />
        </div>

        {/* Summary Stats */}
        <SummaryStats data={data} />

        {/* Risk Distribution */}
        {data.typosquats.length > 0 && (
          <ModuleSection title="Risk Distribution" icon={Shield}>
            <RiskDistribution typosquats={data.typosquats} />
          </ModuleSection>
        )}

        {/* Issues/Warnings */}
        {data.issues && data.issues.length > 0 && (
          <ModuleSection
            title="Warnings"
            icon={AlertTriangle}
            badge={
              <Badge
                variant="outline"
                className="text-xs bg-amber-500/10 text-amber-400 border-amber-500/30"
              >
                {data.issues.length}
              </Badge>
            }
          >
            <div className="space-y-2">
              {data.issues.map((issue, index) => (
                <div
                  key={index}
                  className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30"
                >
                  <AlertTriangle className="h-4 w-4 text-amber-400 mt-0.5 flex-shrink-0" />
                  <span className="text-sm">{issue}</span>
                </div>
              ))}
            </div>
          </ModuleSection>
        )}

        {/* Typosquat Domains List */}
        {data.typosquats.length > 0 && (
          <ModuleSection
            title="Detected Typosquats"
            icon={Globe}
            badge={
              <Badge
                variant="outline"
                className={cn(
                  "text-xs",
                  hasCritical
                    ? "bg-red-500/10 text-red-400 border-red-500/30"
                    : hasThreats
                    ? "bg-amber-500/10 text-amber-400 border-amber-500/30"
                    : "bg-emerald-500/10 text-emerald-400 border-emerald-500/30"
                )}
              >
                {data.typosquats.length}
              </Badge>
            }
          >
            <TyposquatList typosquats={data.typosquats} />
          </ModuleSection>
        )}

        {/* No Threats State */}
        {data.typosquats.length === 0 && (
          <div className="text-center py-12">
            <ShieldCheck className="h-12 w-12 mx-auto mb-4 text-emerald-400" />
            <h3 className="text-lg font-semibold text-emerald-400">No Typosquats Detected</h3>
            <p className="text-sm text-muted-foreground mt-2">
              Checked {data.generated_count.toLocaleString()} permutations - all clear!
            </p>
          </div>
        )}

        {/* Footer with scan time */}
        {data.duration_ms !== undefined && (
          <div className="flex items-center justify-end gap-2 text-xs text-muted-foreground">
            <Clock className="h-3 w-3" />
            Scan completed in {(data.duration_ms / 1000).toFixed(2)}s
          </div>
        )}
      </div>
    </ModuleContainer>
  );
}

TyposquatModuleView.displayName = "TyposquatModuleView";
